import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Globe, CheckCircle, XCircle, Clock,
  ShoppingCart, ExternalLink, Loader2, History, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../lib/supabase';
import { checkAvailability, getTLDPricing } from '../lib/resellerclub';
import SEO from '../components/SEO';

// Mock TLD pricing data
const TLD_DATA = {
  '.com': { price: 12.99, popular: true },
  '.net': { price: 11.99, popular: true },
  '.org': { price: 10.99, popular: false },
  '.io': { price: 39.99, popular: true },
  '.co': { price: 24.99, popular: false },
  '.ai': { price: 89.99, popular: true },
  '.dev': { price: 14.99, popular: true },
  '.app': { price: 14.99, popular: false },
  '.com.tr': { price: 19.99, popular: true },
  '.net.tr': { price: 14.99, popular: false },
  '.org.tr': { price: 12.99, popular: false },
  '.tech': { price: 34.99, popular: false },
  '.online': { price: 29.99, popular: false },
  '.store': { price: 39.99, popular: false },
};

// Availability checker is now implemented in lib/resellerclub.js using real API proxy

/**
 * DomainSearch - Reusable domain search component
 * Can be embedded on Home page or used standalone
 */
export const DomainSearchWidget = ({ compact = false, className = '' }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [realPrices, setRealPrices] = useState({});
  const [searchParams] = useSearchParams();

  const handleSearch = useCallback(async (forcedQuery = null) => {
    const queryToUse = forcedQuery || searchQuery;
    if (!queryToUse.trim()) return;

    // Clean up the input
    let domain = queryToUse.trim().toLowerCase();
    // ... rest of logic

    // Remove protocol and www if present
    domain = domain.replace(/^(https?:\/\/)?(www\.)?/, '');
    // Remove path if present
    domain = domain.split('/')[0];

    // Extract base domain (without TLD)
    const parts = domain.split('.');
    const baseDomain = parts[0];

    if (!baseDomain || baseDomain.length < 2) {
      return;
    }

    setLoading(true);
    setSearched(true);
    setResults([]);

    try {
      // Check multiple TLDs via ResellerClub API
      const tldsToCheck = Object.keys(TLD_DATA);
      let apiResults = await checkAvailability(baseDomain, tldsToCheck);

      // DEMO MODE FALLBACK:
      // If API returns no results (likely due to missing credentials), use demo mode
      if (Object.keys(apiResults).length === 0) {
        console.warn('ResellerClub API likely unavailable or misconfigured. Falling back to Demo Mode.');
        apiResults = tldsToCheck.reduce((acc, tld) => {
          // Deterministic but random-looking availability for demo
          const isAvailable = (baseDomain.length + tld.length) % 3 !== 0;
          acc[`${baseDomain}${tld}`] = { status: isAvailable ? 'available' : 'taken' };
          return acc;
        }, {});
      }

      const domainResults = tldsToCheck.map((tld) => {
        const fullDomain = `${baseDomain}${tld}`;
        const apiData = apiResults[fullDomain];

        return {
          domain: fullDomain,
          tld,
          isAvailable: apiData?.status === 'available',
          price: realPrices[tld] || TLD_DATA[tld].price,
          popular: TLD_DATA[tld].popular
        };
      });

      // Sort: available first, then by popularity, then by price
      domainResults.sort((a, b) => {
        if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
        if (a.popular !== b.popular) return a.popular ? -1 : 1;
        return a.price - b.price;
      });

      setResults(domainResults);

      // Log primary search to Supabase
      const primaryDomain = `${baseDomain}.com`;
      const primaryResult = domainResults.find(r => r.tld === '.com');

      await supabase.from('domain_searches').insert({
        domain_name: primaryDomain,
        user_id: user?.id || null,
        session_id: sessionStorage.getItem('session_id') || crypto.randomUUID(),
        is_available: primaryResult?.isAvailable || false
      });

    } catch (error) {
      console.error('Domain search error:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, user, realPrices]);

  useEffect(() => {
    const fetchPrices = async () => {
      const prices = await getTLDPricing();
      if (prices) setRealPrices(prices);
    };
    fetchPrices();

    // Handle initial search from URL params
    const q = searchParams.get('q');
    if (q) {
      setSearchQuery(q);
      handleSearch(q);
    }
  }, [searchParams, handleSearch]);

  const texts = {
    tr: {
      placeholder: 'Hayalinizdeki domain adını yazın...',
      search: 'Ara',
      available: 'Müsait',
      taken: 'Alınmış',
      checking: 'Kontrol ediliyor...',
      perYear: '/yıl',
      addToCart: 'Sepete Ekle',
      noResults: 'Sonuç bulunamadı',
      searchFirst: 'Alan adı aramak için yukarıya yazın'
    },
    en: {
      placeholder: 'Enter your dream domain name...',
      search: 'Search',
      available: 'Available',
      taken: 'Taken',
      checking: 'Checking...',
      perYear: '/year',
      addToCart: 'Add to Cart',
      noResults: 'No results found',
      searchFirst: 'Enter a domain name above to search'
    }
  };

  const t = texts[language] || texts.tr;

  return (
    <div className={`${className}`}>
      {/* Search Form */}
      <form onSubmit={(e) => {
        e.preventDefault();
        handleSearch();
      }} className="relative">
        <div className={`flex items-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-lg ${compact ? '' : 'shadow-xl'}`}>
          <div className="flex items-center px-4 text-gray-400">
            <Globe className="w-5 h-5" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.placeholder}
            className={`flex-1 bg-transparent border-none focus:ring-0 focus:outline-none text-gray-900 dark:text-white placeholder-gray-400 ${compact ? 'py-3 text-sm' : 'py-4 text-lg'}`}
            data-testid="domain-search-input"
          />
          <button
            type="submit"
            disabled={loading || !searchQuery.trim()}
            className={`bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${compact ? 'px-4 py-3 text-sm' : 'px-6 py-4'}`}
            data-testid="domain-search-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                <span className="hidden sm:inline">{t.search}</span>
              </span>
            )}
          </button>
        </div>
      </form>

      {/* Results */}
      {searched && !compact && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            {loading ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">{t.checking}</p>
              </div>
            ) : results.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
                <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">{t.noResults}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((result, idx) => (
                  <motion.div
                    key={result.domain}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-white dark:bg-gray-900 border rounded-xl p-4 flex items-center justify-between gap-4 ${result.isAvailable
                      ? 'border-green-200 dark:border-green-800'
                      : 'border-gray-200 dark:border-gray-800'
                      }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {result.isAvailable ? (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className={`font-semibold truncate ${result.isAvailable ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                          {result.domain}
                        </p>
                        {result.popular && (
                          <span className="text-xs text-blue-600 dark:text-blue-400">Popular</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`font-bold ${result.isAvailable ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                          ${result.price}
                        </p>
                        <p className="text-xs text-gray-500">{t.perYear}</p>
                      </div>

                      {result.isAvailable ? (
                        <button
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                          onClick={() => {
                            addToCart({
                              id: `domain-${result.domain}`,
                              name: result.domain,
                              type: 'domain',
                              price: result.price,
                              currency: 'USD',
                              metadata: { tld: result.tld }
                            });
                          }}
                        >
                          <ShoppingCart className="w-4 h-4" />
                          <span className="hidden sm:inline">{t.addToCart}</span>
                        </button>
                      ) : (
                        <span className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-500 text-sm font-medium rounded-lg">
                          {t.taken}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

/**
 * DomainSearchPage - Full page domain search with history
 */
const DomainSearchPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [recentSearches, setRecentSearches] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Load recent searches for logged-in users
  useEffect(() => {
    if (user) {
      loadRecentSearches();
    } else {
      setLoadingHistory(false);
    }
  }, [user]);

  const loadRecentSearches = async () => {
    try {
      const { data, error } = await supabase
        .from('domain_searches')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setRecentSearches(data);
      }
    } catch (err) {
      console.error('Error loading search history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const clearHistory = async () => {
    if (!user) return;

    try {
      await supabase
        .from('domain_logs')
        .delete()
        .eq('user_id', user.id);

      setRecentSearches([]);
    } catch (err) {
      console.error('Error clearing history:', err);
    }
  };

  const texts = {
    tr: {
      title: 'Domain Arama',
      subtitle: 'Hayalinizdeki alan adını bulun',
      description: 'Markanız için mükemmel alan adını arayın ve anında müsaitlik durumunu kontrol edin.',
      recentSearches: 'Son Aramalarınız',
      available: 'Müsait',
      taken: 'Alınmış',
      clearHistory: 'Geçmişi Temizle',
      noHistory: 'Henüz arama geçmişiniz yok',
      loginForHistory: 'Arama geçmişinizi görmek için giriş yapın'
    },
    en: {
      title: 'Domain Search',
      subtitle: 'Find your dream domain name',
      description: 'Search for the perfect domain name for your brand and check availability instantly.',
      recentSearches: 'Your Recent Searches',
      available: 'Available',
      taken: 'Taken',
      clearHistory: 'Clear History',
      noHistory: 'No search history yet',
      loginForHistory: 'Sign in to see your search history'
    }
  };

  const t = texts[language] || texts.tr;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={language === 'tr' ? 'Domain Sorgulama & Tescil' : 'Domain Search & Registration'}
        description={language === 'tr' ? 'Hayalinizdeki domain adını bulun ve kaydedin. .com, .net, .org ve daha fazla uzantı seçeneği.' : 'Find and register your dream domain name. .com, .net, .org and more extensions.'}
        keywords="domain sorgulama, alan adı tescil, domain search, buy domain, cplus"
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/80 mb-8"
          >
            {t.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <DomainSearchWidget />
          </motion.div>
        </div>
      </div>

      {/* Recent Searches Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-gray-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t.recentSearches}
            </h2>
          </div>
          {user && recentSearches.length > 0 && (
            <button
              onClick={clearHistory}
              className="text-sm text-red-600 dark:text-red-400 hover:underline flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              {t.clearHistory}
            </button>
          )}
        </div>

        {!user ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">{t.loginForHistory}</p>
          </div>
        ) : loadingHistory ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          </div>
        ) : recentSearches.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
            <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">{t.noHistory}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {recentSearches.map((search, idx) => (
              <motion.div
                key={search.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {search.domain_name}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${search.is_available
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {search.is_available ? t.available : t.taken}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(search.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainSearchPage;
